import type { AllyAdminStatus, AllyStatus, AllyTenantSyncStatus } from '@goelectric/shared-types';
import {
  ALLY_STATUS_META,
  ALLY_TENANT_SYNC_META,
  darkColors,
  neutralColors,
  semanticColors,
} from '@goelectric/ui-components';
import type { StatusIconKey } from '@goelectric/ui-components';
import { StatusBadgeShell, StatusIcon, useStatusColor } from '@/components/StatusBadgeShell';

/** Estado de gestión del aliado: color + icono + texto (WCAG, nunca solo color). */
export function AllyStatusBadge({
  status,
  forceDark = false,
}: {
  status: AllyStatus;
  forceDark?: boolean;
}) {
  const meta = ALLY_STATUS_META[status];
  const color = useStatusColor(meta.color, meta.colorDark, forceDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={meta.label}
      title={`${meta.label}: ${meta.description}`}
    />
  );
}

interface AllyAdminStatusMeta {
  label: string;
  description: string;
  color: string;
  colorDark: string;
  iconKey: StatusIconKey;
}

/**
 * Ciclo de vida de la cuenta del admin (F11). Color + icono + texto: el estado nunca
 * depende solo del color (WCAG). `PENDING` = invitado sin enrolar; `ACTIVE` = ya ingresó.
 */
const ADMIN_STATUS_META: Record<AllyAdminStatus, AllyAdminStatusMeta> = {
  PENDING: {
    label: 'Invitado',
    description: 'Invitado: todavía no completó el enrolamiento.',
    color: semanticColors.warning,
    colorDark: darkColors.warning,
    iconKey: 'info',
  },
  ACTIVE: {
    label: 'Activo',
    description: 'Cuenta operativa: el administrador ya ingresó.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  INACTIVE: {
    label: 'Inactivo',
    description: 'Cuenta desactivada.',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
};

/** Estado de la cuenta de un administrador del aliado (F11). */
export function AllyAdminStatusBadge({ status, forceDark = false }: { status: AllyAdminStatus; forceDark?: boolean }) {
  const meta = ADMIN_STATUS_META[status];
  const color = useStatusColor(meta.color, meta.colorDark, forceDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={meta.label}
      title={`${meta.label}: ${meta.description}`}
    />
  );
}

/** Estado de sincronización del tenant de CitrineOS (ADR 0024). */
export function AllyTenantSyncBadge({
  status,
  forceDark = false,
}: {
  status: AllyTenantSyncStatus;
  forceDark?: boolean;
}) {
  const meta = ALLY_TENANT_SYNC_META[status];
  const color = useStatusColor(meta.color, meta.colorDark, forceDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={meta.label}
      title={`${meta.label}: ${meta.description}`}
    />
  );
}
